import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '@/config/firebase';
import { useTenantStore } from '@/store/tenantStore';
import { useAuthStore } from '@/store/authStore';
import { useLocalizationStore } from '@/store/localizationStore';
import {
  searchJobs,
  getSearchAggregations,
  getJobCounts,
  getAppliedSavedJobs,
  getSavedJobsCount,
  saveJob,
  unsaveJob,
} from '@/services/api/homeApi';
import { getUserDetails, prefetchDynamicForms } from '@/services/api/authApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import {
  applyFilterSelection,
  countAppliedFilters,
  defaultSortFilters,
  sortSelectedOnTop,
  withCheckedState,
  type SelectedFilters,
} from '@/features/jobs/utils/filterState';
import {
  readAppliedSavedCache,
  withAppliedSaved,
  writeAppliedSavedCache,
} from '../utils/appliedSavedCache';
import { appendNewJobs } from '@/features/jobs/utils/jobPages';
import type {
  SearchInput,
  SearchResponse,
  Aggregation,
  AggregationValue,
  JobEntity,
} from '@/types/job.types';
import type { CandidateDetails } from '@/services/api/authApi';
import type { LoginReturnInput } from '@/services/auth/postLoginRedirect';
import { redirectToSignIn, SIGN_IN_TO_SAVE_JOB } from '@/features/jobs/utils/signInRequired';

// Toast texts — Flutter ErrorConstants
const SAVE_JOB_SUCCESS = 'Saved the job successfully';
const SAVE_JOB_FAILED = 'Failed to save the job!';
const UNSAVE_JOB_SUCCESS = 'Unsaved the job successfully';
const UNSAVE_JOB_FAILED = 'Failed to unsave the job!';

/** Flutter WebHomeBloc: `dynamicFormsContent` is only fetched when a cached schema is missing. */
function isDynamicFormCacheMissing(): boolean {
  return !localStorage.getItem('applyFormContent') || !localStorage.getItem('editProfileFormContent');
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const PAGE_SIZE = 6;

export interface HomeState {
  /** Search input driving queries */
  searchInput: SearchInput;
  /** Current search response entities (accumulated for infinite scroll) */
  entities: JobEntity[];
  /** Aggregations with the selected filters marked as checked (checked values first) */
  aggregations: Aggregation[];
  /** Category tab data (from selected aggregation) */
  categoryData: AggregationValue[];
  /** Search total for the current results */
  totalCount: number;

  /** 0 = All Jobs, 1 = Hot Jobs */
  exploreTabIndex: number;
  /** Current category tab index (Department / Skills / Experience) */
  categoryTabIndex: number;

  /** Tab badges — Flutter _GetJobsCounts once, then the active tab follows the search total */
  allJobsCount: number;
  hotJobsCount: number;
  savedJobsCount: number;

  /** Category "View All" toggle per index */
  showCategoryViewAll: boolean[];

  /** Filter panel visibility */
  isFilterVisible: boolean;
  /** Applied filter count */
  appliedFilterCount: number;
  /** Selected filters */
  selectedFilters: SelectedFilters;

  /** Pagination */
  currentPage: number;
  hasMore: boolean;

  /** Loading states */
  isLoadingJobs: boolean;
  isLoadingCategories: boolean;
  isLoadingMore: boolean;

  /** Candidate details (fetched on init if logged in) */
  candidateDetails: CandidateDetails | null;

  /** Toast messages */
  toast: { message: string; type: 'success' | 'error' } | null;
}

export interface HomeActions {
  onSelectCategoryTab: (index: number) => void;
  onSelectExploreTab: (index: number) => void;
  onSearch: (input: SearchInput) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  /** Filter checkbox changes — Flutter onFilterSelection */
  onFilterSelection: (values: AggregationValue[]) => void;
  /** Sort chosen from the sort menu — applied immediately */
  onSelectSort: (value: AggregationValue) => void;
  onClearSingleFilter: (key: string) => void;
  onToggleFilterPanel: () => void;
  onToggleCategoryViewAll: (index: number) => void;
  onSaveJob: (entity: JobEntity, index: number) => void;
  onUnsaveJob: (entity: JobEntity, index: number) => void;
  onLoadMore: () => void;
  clearToast: () => void;
}

export function useHome(): { state: HomeState; actions: HomeActions } {
  const { t } = useTranslation();
  const getJobCategories = useTenantStore((s) => s.getJobCategories);
  const user = useAuthStore((s) => s.user);
  const currentLanguage = useLocalizationStore((s) => s.currentLanguage);
  const jobCategories = getJobCategories();
  const navigate = useNavigate();
  const location = useLocation();
  // Set by login/MFA after an anonymous save (Flutter ProfileInput.saveJobInProgress)
  const saveJobInProgress = !!(location.state as { profileInput?: LoginReturnInput } | null)
    ?.profileInput?.saveJobInProgress;

  // ── Core state ──────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState<SearchInput>({
    isHotJob: false,
    switchIndex: 0,
  });
  const [entities, setEntities] = useState<JobEntity[]>([]);
  const [rawAggregations, setRawAggregations] = useState<Aggregation[]>([]);

  const [exploreTabIndex, setExploreTabIndex] = useState(0);
  const [categoryTabIndex, setCategoryTabIndex] = useState(0);

  const [allJobsCount, setAllJobsCount] = useState(0);
  const [hotJobsCount, setHotJobsCount] = useState(0);
  const [savedJobsCount, setSavedJobsCount] = useState(0);

  const [showCategoryViewAll, setShowCategoryViewAll] = useState([
    false,
    false,
    false,
  ]);

  // Flutter WebHomeState.initial: isShowFilter = true (the filter toggle hides the row)
  const [isFilterVisible, setIsFilterVisible] = useState(true);
  const [appliedFilterCount, setAppliedFilterCount] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});

  // The list/card toggle (Flutter isCardView) isn't ported: the list is the left column of the split view
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [candidateDetails, setCandidateDetails] =
    useState<CandidateDetails | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const isInitialized = useRef(false);
  const saveAfterLoginDone = useRef(false);
  /** A next-page request is on its way (scroll events can fire again before React re-renders) */
  const isFetchingMore = useRef(false);
  /** Ignores responses from a search that a newer one replaced */
  const searchSeq = useRef(0);
  /** The latest page request; only it may clear the loading flags */
  const latestRequest = useRef(0);

  // Flutter _SetFilterBackup: checked state always follows the selected filters
  const aggregations = useMemo(
    () => withCheckedState(rawAggregations, selectedFilters),
    [rawAggregations, selectedFilters],
  );
  const categoryKey = jobCategories[categoryTabIndex]?.key;
  const categoryData = useMemo(
    () => rawAggregations.find((a) => a.name === categoryKey)?.values ?? [],
    [rawAggregations, categoryKey],
  );

  const refreshSavedJobsCount = useCallback(() => {
    getSavedJobsCount()
      .then((c) => setSavedJobsCount(c))
      .catch(() => {});
  }, []);

  // ── Complete save-after-login — mirrors Flutter WebHomeBloc._OnClickSaveJobAfterLogin ──
  const completeSaveAfterLogin = useCallback(async (details: CandidateDetails) => {
    const saveJobJson = localStorage.getItem('saveJobDetails');
    if (!saveJobJson || !auth.currentUser) return;

    try {
      const decodedMap = JSON.parse(saveJobJson) as Record<string, unknown>;
      if (!decodedMap.requisitionId) return;

      await saveJob({
        candidateId: (details as Record<string, unknown>).id,
        requisitionId: decodedMap.requisitionId,
        candidateDisplayId: (details as Record<string, unknown>).displayId,
        requisitionDisplayId: decodedMap.requisitionDisplayId,
        primaryEmailId: details.primaryEmailId,
        exprncFromMnthInYears: decodedMap.exprncFromMnthInYears,
        exprncToMnthInYears: decodedMap.exprncToMnthInYears,
      });

      setEntities((prev) =>
        prev.map((e) =>
          e.displayId === decodedMap.requisitionDisplayId ? { ...e, isSavedJob: true } : e,
        ),
      );
      setToast({ message: t(SAVE_JOB_SUCCESS), type: 'success' });
      localStorage.removeItem('saveJobDetails');
      refreshSavedJobsCount();
    } catch {
      setToast({ message: t(SAVE_JOB_FAILED), type: 'error' });
    }
  }, [t, refreshSavedJobsCount]);

  // ── Candidate details, dynamic forms & saved count (when logged in) — Flutter _Init ──
  useEffect(() => {
    if (!auth.currentUser) return;
    getUserDetails()
      .then((d) => {
        setCandidateDetails(d);
        if (saveJobInProgress && !saveAfterLoginDone.current) {
          saveAfterLoginDone.current = true;
          completeSaveAfterLogin(d);
        }
      })
      .catch(() => {});
    if (isDynamicFormCacheMissing()) void prefetchDynamicForms();
    if (!saveJobInProgress) refreshSavedJobsCount();
  }, [user, saveJobInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Search jobs ─────────────────────────────────────────────

  const fetchJobs = useCallback(
    async (input: SearchInput, page: number, append = false) => {
      const seq = append ? searchSeq.current : ++searchSeq.current;
      const requestId = ++latestRequest.current;
      if (page === 1) setIsLoadingJobs(true);
      else setIsLoadingMore(true);

      try {
        const res: SearchResponse = await searchJobs(input, page, PAGE_SIZE);
        if (seq !== searchSeq.current) return;
        const total = res.total ?? 0;
        const totalPages = Math.ceil(total / PAGE_SIZE);
        setTotalCount(total);
        // Flutter _GetSearchResults: the active tab's badge becomes the search total
        if (!append) {
          if (input.isHotJob) setHotJobsCount(total);
          else setAllJobsCount(total);
        }

        // Flutter _GetSavedAppliedJobs: cached applied/saved statuses first, then the network result
        const list = withAppliedSaved(res.entities, readAppliedSavedCache());
        if (append) {
          // A job the previous pages already listed is skipped (jobPages.ts)
          setEntities((prev) => appendNewJobs(prev, list));
        } else {
          setEntities(list);
        }

        setHasMore(page < totalPages);
        setIsLoadingJobs(false);
        setIsLoadingMore(false);
        isFetchingMore.current = false;

        if (auth.currentUser) {
          try {
            const appliedSaved = await getAppliedSavedJobs();
            if (seq !== searchSeq.current) return;
            writeAppliedSavedCache(appliedSaved);
            setEntities((prev) => withAppliedSaved(prev, appliedSaved));
          } catch {
            // Non-critical — the cached flags stay
          }
        }
      } catch {
        if (seq !== searchSeq.current) return;
        setEntities((prev) => (append ? prev : []));
        setHasMore(false);
      } finally {
        // A later page may already be loading (isFetchingMore is cleared once a page is in)
        if (seq === searchSeq.current && requestId === latestRequest.current) {
          setIsLoadingJobs(false);
          setIsLoadingMore(false);
          isFetchingMore.current = false;
        }
      }
    },
    [],
  );

  // ── Fetch aggregations — Flutter _GetSearchResultAggregation (always an empty SearchInput) ──

  const fetchAggregations = useCallback(async (filters: SelectedFilters) => {
    setIsLoadingCategories(true);
    try {
      const res = await getSearchAggregations({});
      setRawAggregations(sortSelectedOnTop(res.aggregations ?? [], filters));
      // Flutter re-selects the first "Jobs by category" tab whenever aggregations load
      setCategoryTabIndex(0);
    } catch {
      setRawAggregations([]);
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  /** Run a page-1 search with the given filters (Flutter _OnApplyFilters). */
  const runSearch = useCallback(
    (input: SearchInput, filters: SelectedFilters) => {
      const updated: SearchInput = { ...input, filters };
      setAppliedFilterCount(countAppliedFilters(filters));
      setSearchInput(updated);
      setCurrentPage(1);
      fetchJobs(updated, 1);
      fetchAggregations(filters);
      return updated;
    },
    [fetchJobs, fetchAggregations],
  );

  // ── Init ────────────────────────────────────────────────────

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    // Flutter _Init: getJobsCounts(SearchInput()) once, with no query params. After that the
    // active tab's badge follows the search total (fetchJobs)
    getJobCounts({})
      .then((res) => {
        setAllJobsCount(res.totalCount ?? 0);
        setHotJobsCount(res.hotJobCount ?? 0);
      })
      .catch(() => {});

    const initialFilters = defaultSortFilters(false);
    setSelectedFilters(initialFilters);
    runSearch({ isHotJob: false, switchIndex: 0 }, initialFilters);
  }, [runSearch]);

  // ── Language change — Flutter languageListener → _OnRefreshData ──
  const languageRef = useRef(currentLanguage);
  useEffect(() => {
    if (languageRef.current === currentLanguage) return;
    languageRef.current = currentLanguage;
    runSearch(searchInput, selectedFilters);
  }, [currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ─────────────────────────────────────────────────

  const onSelectCategoryTab = useCallback((index: number) => {
    setCategoryTabIndex(index);
  }, []);

  // Flutter: clearFilters(isLoading: false) then onClickAllJobs / onClickHotJobs
  const onSelectExploreTab = useCallback(
    (index: number) => {
      if (index === exploreTabIndex) return;
      setExploreTabIndex(index);
      const filters = defaultSortFilters(false);
      setSelectedFilters(filters);
      runSearch(
        {
          ...searchInput,
          categoryDetails: undefined,
          isHotJob: index === 1,
        },
        filters,
      );
    },
    [exploreTabIndex, searchInput, runSearch],
  );

  const onSearch = useCallback(
    (input: SearchInput) => {
      setCurrentPage(1);
      setSearchInput(input);
      fetchJobs(input, 1);
    },
    [fetchJobs],
  );

  const onApplyFilters = useCallback(() => {
    void analyticsService.logJobSearch({ searchTerm: 'apply filter', filters: selectedFilters });
    runSearch(searchInput, selectedFilters);
  }, [selectedFilters, searchInput, runSearch]);

  const onClearFilters = useCallback(() => {
    const filters = defaultSortFilters(false);
    setSelectedFilters(filters);
    runSearch(
      {
        ...searchInput,
        filters: undefined,
        categoryDetails: undefined,
        isHotJob: exploreTabIndex === 1,
      },
      filters,
    );
    void analyticsService.logEvent('home_page', 'clear_filter');
  }, [searchInput, exploreTabIndex, runSearch]);

  const onFilterSelection = useCallback((values: AggregationValue[]) => {
    setSelectedFilters((prev) => applyFilterSelection(prev, values));
  }, []);

  const onSelectSort = useCallback(
    (value: AggregationValue) => {
      const filters = applyFilterSelection(selectedFilters, [value]);
      setSelectedFilters(filters);
      void analyticsService.logJobSearch({ searchTerm: 'apply filter', filters });
      runSearch(searchInput, filters);
    },
    [selectedFilters, searchInput, runSearch],
  );

  // Flutter _OnClearFilter — removes one category; the search reruns on Apply
  const onClearSingleFilter = useCallback((key: string) => {
    setSelectedFilters((prev) => {
      if (!(key in prev)) return prev;
      const updated = { ...prev };
      const removed = updated[key]?.length ?? 0;
      delete updated[key];
      setAppliedFilterCount((c) => Math.max(0, c - removed));
      return updated;
    });
  }, []);

  const onToggleFilterPanel = useCallback(() => {
    setIsFilterVisible((v) => !v);
  }, []);

  const onToggleCategoryViewAll = useCallback((index: number) => {
    setShowCategoryViewAll((prev) => {
      const copy = [...prev];
      copy[index] = !copy[index];
      return copy;
    });
  }, []);

  const setSaveLoading = useCallback((index: number, loading: boolean, patch: Partial<JobEntity> = {}) => {
    setEntities((prev) => {
      const copy = [...prev];
      if (copy[index]) copy[index] = { ...copy[index], ...patch, isLoadingForSaveJobButton: loading };
      return copy;
    });
  }, []);

  const onSaveJob = useCallback(
    async (entity: JobEntity, index: number) => {
      if (!auth.currentUser) {
        // Anonymous save — store details and redirect to login (Flutter WebHomeBloc._OnClickSaveJob)
        const saveJobDetails = {
          requisitionId: entity.id,
          requisitionDisplayId: entity.displayId,
          exprncFromMnthInYears: entity.requiredExperienceInMonths?.from,
          exprncToMnthInYears: entity.requiredExperienceInMonths?.to,
        };
        localStorage.setItem('saveJobDetails', JSON.stringify(saveJobDetails));
        redirectToSignIn(navigate, SIGN_IN_TO_SAVE_JOB, { saveJobInProgress: true });
        return;
      }

      setSaveLoading(index, true);
      try {
        const details = candidateDetails ?? (await getUserDetails());
        if (!candidateDetails) setCandidateDetails(details);
        await saveJob({
          candidateId: (details as Record<string, unknown>).id,
          requisitionId: entity.id,
          candidateDisplayId: (details as Record<string, unknown>).displayId,
          requisitionDisplayId: entity.displayId,
          primaryEmailId: details.primaryEmailId,
          exprncFromMnthInYears: entity.requiredExperienceInMonths?.from,
          exprncToMnthInYears: entity.requiredExperienceInMonths?.to,
        });
        void analyticsService.logJobSave({
          location: 'home_page',
          jobId: entity.id ?? '',
          jobTitle: entity.jobTitle ?? '',
        });
        setSaveLoading(index, false, { isSavedJob: true });
        setToast({ message: t(SAVE_JOB_SUCCESS), type: 'success' });
      } catch {
        setSaveLoading(index, false);
        setToast({ message: t(SAVE_JOB_FAILED), type: 'error' });
      }
      refreshSavedJobsCount();
    },
    [candidateDetails, navigate, setSaveLoading, t, refreshSavedJobsCount],
  );

  const onUnsaveJob = useCallback(
    async (entity: JobEntity, index: number) => {
      setSaveLoading(index, true);
      try {
        await unsaveJob(entity.displayId ?? '');
        setSaveLoading(index, false, { isSavedJob: false });
        setToast({ message: t(UNSAVE_JOB_SUCCESS), type: 'success' });
      } catch {
        setSaveLoading(index, false);
        setToast({ message: t(UNSAVE_JOB_FAILED), type: 'error' });
      }
      void analyticsService.logJobUnsave({
        location: 'home_page',
        jobId: entity.id ?? '',
        jobTitle: entity.jobTitle ?? '',
      });
      refreshSavedJobsCount();
    },
    [setSaveLoading, t, refreshSavedJobsCount],
  );

  const onLoadMore = useCallback(() => {
    if (!hasMore || isFetchingMore.current || isLoadingMore || isLoadingJobs) return;
    isFetchingMore.current = true;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchJobs(searchInput, nextPage, true);
  }, [hasMore, isLoadingMore, isLoadingJobs, currentPage, searchInput, fetchJobs]);

  const clearToast = useCallback(() => setToast(null), []);

  return {
    state: {
      searchInput,
      entities,
      aggregations,
      categoryData,
      totalCount,
      exploreTabIndex,
      categoryTabIndex,
      allJobsCount,
      hotJobsCount,
      savedJobsCount,
      showCategoryViewAll,
      isFilterVisible,
      appliedFilterCount,
      selectedFilters,
      currentPage,
      hasMore,
      isLoadingJobs,
      isLoadingCategories,
      isLoadingMore,
      candidateDetails,
      toast,
    },
    actions: {
      onSelectCategoryTab,
      onSelectExploreTab,
      onSearch,
      onApplyFilters,
      onClearFilters,
      onFilterSelection,
      onSelectSort,
      onClearSingleFilter,
      onToggleFilterPanel,
      onToggleCategoryViewAll,
      onSaveJob,
      onUnsaveJob,
      onLoadMore,
      clearToast,
    },
  };
}
